import React from "react";
import ReactDOM from "react-dom/client";
import "@societea/tokens/tokens.css";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { migrateLegacyCookiesOnce } from "util/migrateCookies";
import { setDefaultSiteOg } from "util/ogMetadataUtils";
import { registerNotifyResolver } from "lib/notify";
import { commentNotifyResolver } from "i18n/commentUi";

interface WindowWithSocieteaSplash extends Window {
  __hideSocieteaSplash?: () => void;
}

/**
 * Apply default social metadata on first load.
 * Steps:
 * 1) Ensure the DOM is available before touching meta tags.
 * 2) Apply canonical/OG/Twitter defaults for the initial URL.
 */
const applyDefaultSocialMeta = (): void => {
  // Step 1: Guard against non-browser environments.
  if (typeof document === "undefined") {
    return;
  }

  // Step 2: Apply safe defaults without blocking rendering.
  try {
    setDefaultSiteOg({ url: window.location.href });
  } catch (error) {
    // Intentionally swallow metadata errors to keep boot resilient.
  }
};

/**
 * Hide the splash screen if the hook exists.
 * Steps:
 * 1) Read the optional hook from window.
 * 2) Validate it is a function.
 * 3) Invoke safely without breaking render.
 */
const hideSocieteaSplashIfAvailable = (): void => {
  // Step 1: Read the optional hook.
  const windowWithSplash = window as WindowWithSocieteaSplash;
  const hideSplash = windowWithSplash.__hideSocieteaSplash;

  // Step 2: Validate the hook before calling.
  if (typeof hideSplash !== "function") {
    return;
  }

  // Step 3: Invoke inside a try/catch to avoid blocking render.
  try {
    hideSplash();
  } catch (error) {
    // Intentionally ignore splash teardown failures.
  }
};

// Migrate legacy cookies to localStorage on app initialization.
migrateLegacyCookiesOnce();
applyDefaultSocialMeta();

// Register notify locale resolvers for each domain that ships
// localized toast keys. Adding a new resolver here is the standard
// way to expose `notify.success({ key: "domain.action" })` callsites.
registerNotifyResolver(commentNotifyResolver);

// Ensure the root element exists before mounting React.
const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element #root was not found in index.html.");
}

const root = ReactDOM.createRoot(rootElement);
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// Hide splash once React has mounted a frame.
hideSocieteaSplashIfAvailable();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
